/*
 * How a design with several pages is kept in its JSON (one file, so saving and the version check stay as they were):
 *
 *   { "version": "5.3.0", "objects": [ ...page 1... ], "size": { "w": 1080, "h": 1080 }, "print": { "bleedMm": 3 },
 *     "extraPages": [ { "objects": [ ...page 2... ], "w": 2480, "h": 3508, "bleedMm": 0 }, ... ] }
 *
 * Each page has its own size and print bleed (หน้าหลายขนาด, 18 ต.ค. 2569): page 1's in `size` / `print.bleedMm`
 * (`size` is also the design's width/height columns in D1), the others' on their own entry. A design from before
 * that has no `size` and no w/h on its pages: every page is the design's width × height and page 1's bleed.
 * The app always writes them all; `size` tells the server this app knows page sizes (worker/rules.ts).
 *
 * Page 1 stays at the top level, so the JSON is still a plain Fabric canvas (Fabric ignores the other keys)
 * and designs saved before pages existed are simply 1-page designs. The app always writes `extraPages`
 * (even []), which lets the server tell this app from an older one that only knows page 1 (worker/rules.ts).
 * Pages are kept as JSON text of their objects array, so a page is copied without being re-read.
 *
 * Page names (ตั้งชื่อหน้า, 19 ต.ค. 2569): `pageNames` at the top level, one string per page ('' = no name). The app writes
 * it once it knows the names of the design (it was in the JSON it opened, or a name was set); an app from before
 * names never writes it, so the server refuses its save over a design whose pages have names (D1 designs.page_names,
 * 0013) — the names would be gone. The same refusal covers work an old app left on a device (BUG-39's second path).
 *
 * Guides (เส้นไกด์จากไม้บรรทัด, 22 ต.ค. 2569): `guides` at the top level, one `{ x: [...], y: [...] }` per page — the
 * positions of the page's vertical (x) and horizontal (y) guides in design px from the page's top-left corner (the trim,
 * not the bleed). On the screen only, never in a file. Written once the app knows the design's guides (the JSON had
 * them, or a guide was added); an app from before guides drops the key, so the server refuses its save over a design
 * that has guides (D1 designs.has_guides, 0017) — the same way as page names.
 */

export const MAX_PAGES = 50;
/** longest page name (letters, as the person sees them) */
export const MAX_PAGE_NAME = 40;
/** same limits as the size settings (src/ui/docSheet.ts) and the server */
export const MIN_SIDE_PX = 16, MAX_SIDE_PX = 20000;
/** same as src/print/rules.ts MAX_BLEED_MM (not imported: the server reads this file too) */
const MAX_BLEED = 10;

/** a page's size in design px and its print bleed in mm (undefined = never set, the same as 0) */
export interface PageSize { w: number; h: number; bleedMm?: number }

/** why a page's own size/bleed in the JSON cannot be used (`what` names the page), or null */
export function pageSizeProblem(v: { w?: unknown; h?: unknown; bleedMm?: unknown }, what: string): string | null {
  if ((v.w === undefined) !== (v.h === undefined)) return `${what}มีแค่ความกว้างหรือความสูงอย่างเดียว`;
  if (v.w !== undefined) {
    const ok = (n: unknown) => typeof n === 'number' && Number.isInteger(n) && n >= MIN_SIDE_PX && n <= MAX_SIDE_PX;
    if (!ok(v.w) || !ok(v.h)) return `${what}ต้องเป็นจำนวนเต็ม ${MIN_SIDE_PX} ถึง ${MAX_SIDE_PX.toLocaleString('en-US')} พิกเซลต่อด้าน`;
  }
  if (v.bleedMm !== undefined && !(typeof v.bleedMm === 'number' && Number.isFinite(v.bleedMm) && v.bleedMm >= 0 && v.bleedMm <= MAX_BLEED)) {
    return `ระยะตัดตกของ${what.replace(/^ขนาด/, '')}ต้องอยู่ระหว่าง 0 ถึง ${MAX_BLEED} mm`;
  }
  return null;
}

/** why `pageNames` in a design JSON cannot be used, or null (also used by the server) */
export function pageNamesProblem(v: unknown, pages: number): string | null {
  if (v === undefined) return null;
  if (!Array.isArray(v) || v.length !== pages) return 'รายการชื่อหน้าในเนื้อหางานไม่ตรงกับจำนวนหน้า';
  for (const [k, n] of v.entries()) {
    if (typeof n !== 'string') return `ชื่อหน้า ${k + 1} ในเนื้อหางานเสีย`;
    if ([...n].length > MAX_PAGE_NAME) return `ชื่อหน้า ${k + 1} ยาวเกิน ${MAX_PAGE_NAME} ตัวอักษร`;
  }
  return null;
}

/* ─── guides ──────────────────────────────────────────────────────────────────────────────────────────────────── */

/** one page's guides: x = vertical lines, y = horizontal lines (design px from the page's top-left) */
export interface PageGuides { x: number[]; y: number[] }
/** most guides one page may have on each axis */
export const MAX_GUIDES = 40;
/** how far outside the page a guide may be kept (design px) — the biggest page plus room for the bleed */
const GUIDE_REACH = MAX_SIDE_PX;
export const noGuides = (): PageGuides => ({ x: [], y: [] });

/** why `guides` in a design JSON cannot be used, or null (also used by the server) */
export function guidesProblem(v: unknown, pages: number): string | null {
  if (v === undefined) return null;
  if (!Array.isArray(v) || v.length !== pages) return 'รายการเส้นไกด์ในเนื้อหางานไม่ตรงกับจำนวนหน้า';
  for (const [k, g] of v.entries()) {
    const ok = g && typeof g === 'object' && Array.isArray((g as PageGuides).x) && Array.isArray((g as PageGuides).y);
    if (!ok) return `เส้นไกด์ของหน้า ${k + 1} ในเนื้อหางานเสีย`;
    for (const axis of ['x', 'y'] as const) {
      const list = (g as PageGuides)[axis];
      if (list.length > MAX_GUIDES) return `หน้า ${k + 1} มีเส้นไกด์${axis === 'x' ? 'แนวตั้ง' : 'แนวนอน'}เกิน ${MAX_GUIDES} เส้น`;
      if (!list.every(n => typeof n === 'number' && Number.isFinite(n) && Math.abs(n) <= GUIDE_REACH)) return `ตำแหน่งเส้นไกด์ของหน้า ${k + 1} ในเนื้อหางานเสีย`;
    }
  }
  return null;
}

/** whether a design JSON has any guide (D1 designs.has_guides; read for every save and copy) */
export function hasGuides(json: string): boolean {
  try {
    const g = (JSON.parse(json) as { guides?: unknown }).guides;
    return Array.isArray(g) && g.some(p => p && typeof p === 'object' && ((p as PageGuides).x?.length > 0 || (p as PageGuides).y?.length > 0));
  } catch { return false; }
}

/** where page `k` is after the page at `from` is moved to place `to` (ลากเรียงหน้า) */
export function movedIndex(k: number, from: number, to: number): number {
  if (k === from) return to;
  if (from < to && k > from && k <= to) return k - 1;
  if (from > to && k < from && k >= to) return k + 1;
  return k;
}

/**
 * the place a held page goes (ลากเรียงหน้า): how many of the OTHER pages' centres are left of the finger. The held
 * page itself is left out — it moves with the finger, so measuring it pinned every drop to the right of where it
 * started when it was held by its right half (BUG-49).
 */
export const dropPlace = (otherCentres: number[], x: number) => otherCentres.filter(c => c < x).length;

/**
 * whether the app knows the design's page names after putting a JSON on the canvas: an opened design decides it; an
 * undo/redo step never turns it off (a step from before the first name has none — BUG-48)
 */
export const namesKnownAfter = (before: boolean, jsonHasNames: boolean, undoStep: boolean) => undoStep ? before || jsonHasNames : jsonHasNames;

/** a page name as it is kept: one line, spaces tidied */
export const cleanPageName = (s: string) => s.replace(/\s+/g, ' ').trim();

/** whether a design JSON has any page name (D1 designs.page_names; the server reads this for every save) */
export function hasPageNames(json: string): boolean {
  try {
    const n = (JSON.parse(json) as { pageNames?: unknown }).pageNames;
    return Array.isArray(n) && n.some(x => typeof x === 'string' && x !== '');
  } catch { return false; }
}

export interface SplitDesign {
  /** everything except the pages (version, print settings, …) */
  meta: Record<string, unknown>;
  /** each page's objects, as JSON text of an array */
  pages: string[];
  /** each page's own size/bleed as written in the JSON (empty = not written: see sizesOf) */
  sizes: Array<Partial<PageSize>>;
  /** each page's name ('' = none); null when the JSON has no `pageNames` (written by an app from before names) */
  names: string[] | null;
  /** each page's guides; null when the JSON has no `guides` (written by an app from before guides) */
  guides: PageGuides[] | null;
}

/** Split a design JSON into its pages. Throws with a Thai reason when it is not a design. */
export function splitPages(json: string): SplitDesign {
  let data: Record<string, unknown>;
  try { data = JSON.parse(json); } catch { throw new Error('เนื้อหางานเสีย อ่านไม่ได้'); }
  if (!data || typeof data !== 'object' || !Array.isArray(data.objects)) throw new Error('เนื้อหางานไม่มีรายการวัตถุ');
  const { objects, extraPages, pageNames, guides, ...meta } = data;
  const pages = [JSON.stringify(objects)];
  const size = (meta.size ?? {}) as { w?: unknown; h?: unknown };
  const print = (meta.print ?? {}) as { bleedMm?: unknown };
  // page 1's bleed: an unusable value counts as none, as before page sizes (src/print/rules.ts bleedIn)
  const b1 = typeof print.bleedMm === 'number' && Number.isFinite(print.bleedMm) && print.bleedMm >= 0 && print.bleedMm <= MAX_BLEED ? print.bleedMm : undefined;
  const first = { w: size.w, h: size.h, bleedMm: b1 };
  const why = pageSizeProblem(first, 'ขนาดหน้า 1 ');
  if (why) throw new Error(why);
  const sizes: Array<Partial<PageSize>> = [first as Partial<PageSize>];
  if (extraPages !== undefined) {
    if (!Array.isArray(extraPages)) throw new Error('รายการหน้าของงานเสีย');
    extraPages.forEach((p, k) => {
      if (!p || !Array.isArray((p as { objects?: unknown }).objects)) throw new Error('หน้าหนึ่งในงานเสีย (ไม่มีรายการวัตถุ)');
      const e = p as { objects: unknown[]; w?: unknown; h?: unknown; bleedMm?: unknown };
      const bad = pageSizeProblem(e, `ขนาดหน้า ${k + 2} `);
      if (bad) throw new Error(bad);
      pages.push(JSON.stringify(e.objects));
      sizes.push({ w: e.w, h: e.h, bleedMm: e.bleedMm } as Partial<PageSize>);
    });
  }
  const bad = pageNamesProblem(pageNames, pages.length) ?? guidesProblem(guides, pages.length);
  if (bad) throw new Error(bad);
  return {
    meta, pages, sizes, names: pageNames === undefined ? null : (pageNames as string[]).slice(),
    guides: guides === undefined ? null : (guides as PageGuides[]).map(g => ({ x: g.x.slice(), y: g.y.slice() }))
  };
}

/**
 * Every page's size, filling what the JSON does not say: page 1 from `fallback` (the design's width × height),
 * the other pages from page 1 (a design from before page sizes: all pages the same, one bleed).
 */
export function sizesOf(split: Pick<SplitDesign, 'sizes'>, fallback: PageSize): PageSize[] {
  const f = split.sizes[0] ?? {};
  const one: PageSize = { w: f.w ?? fallback.w, h: f.h ?? fallback.h, bleedMm: f.bleedMm ?? fallback.bleedMm };
  return split.sizes.map((s, k) => k === 0 ? one : { w: s.w ?? one.w, h: s.h ?? one.h, bleedMm: s.bleedMm !== undefined ? s.bleedMm : one.bleedMm });
}

/** each page of a design with its own size and bleed, as `base` (name, background…) otherwise */
export function pageDocs<T extends PageSize>(json: string, base: T): T[] {
  return sizesOf(splitPages(json), base).map(s => ({ ...base, w: s.w, h: s.h, bleedMm: s.bleedMm }));
}

/**
 * A design JSON written by an app from before page sizes (no `size`) that has several pages: that app showed and
 * saved every page at page 1's size, so it must not replace a design whose pages differ (src/sync/saver.ts checkRestored).
 */
export function fromAppWithoutSizes(json: string): boolean {
  try {
    const d = JSON.parse(json) as { size?: unknown; extraPages?: unknown };
    return d.size === undefined && Array.isArray(d.extraPages) && d.extraPages.length > 0;
  } catch { return false; }
}

/** whether the pages are not all the same size and bleed */
export const mixedSizes = (sizes: PageSize[]) =>
  sizes.some(s => s.w !== sizes[0].w || s.h !== sizes[0].h || (s.bleedMm ?? 0) !== (sizes[0].bleedMm ?? 0));

/**
 * Put the pages back into one design JSON (page 1 at the top level, the rest in extraPages).
 * With `sizes`, pages 2+ get their own w/h/bleedMm (0 written for "none", so it is never taken from page 1);
 * page 1's size and bleed are in `meta` (size, print) — history.ts writes them.
 */
export function joinPages(meta: Record<string, unknown>, pages: string[], sizes?: PageSize[], names?: string[] | null, guides?: PageGuides[] | null): string {
  if (!pages.length) throw new Error('งานต้องมีอย่างน้อย 1 หน้า');
  if (sizes && sizes.length !== pages.length) throw new Error('จำนวนขนาดหน้าไม่ตรงกับจำนวนหน้า');
  if (names && names.length !== pages.length) throw new Error('จำนวนชื่อหน้าไม่ตรงกับจำนวนหน้า');
  if (guides && guides.length !== pages.length) throw new Error('จำนวนชุดเส้นไกด์ไม่ตรงกับจำนวนหน้า');
  const { pageNames: _old, guides: _oldGuides, ...rest } = meta;
  const head = JSON.stringify({ ...rest, ...(names ? { pageNames: names } : {}), ...(guides ? { guides } : {}), objects: 0, extraPages: 0 });
  const own = (k: number) => sizes ? `,"w":${Math.round(sizes[k].w)},"h":${Math.round(sizes[k].h)},"bleedMm":${sizes[k].bleedMm ?? 0}` : '';
  // splice the page texts in without parsing them again (functions, not strings: page text may contain "$1", "$&"…)
  return head
    .replace('"objects":0', () => `"objects":${pages[0]}`)
    .replace('"extraPages":0', () => `"extraPages":[${pages.slice(1).map((p, k) => `{"objects":${p}${own(k + 1)}}`).join(',')}]`);
}

/** One page as a plain Fabric canvas JSON (what the exporters and the thumbnail renderer load). */
export function pageJson(json: string, index: number): string {
  const { meta, pages } = splitPages(json);
  const p = pages[index];
  if (p === undefined) throw new Error(`ไม่มีหน้า ${index + 1} ในงานนี้`);
  return `{"version":${JSON.stringify(meta.version ?? '5.3.0')},"objects":${p}}`;
}

/** Every page as a plain Fabric canvas JSON. */
export function allPageJson(json: string): string[] {
  const { meta, pages } = splitPages(json);
  return pages.map(p => `{"version":${JSON.stringify(meta.version ?? '5.3.0')},"objects":${p}}`);
}

export const pageCount = (json: string) => splitPages(json).pages.length;

/**
 * The design (or one page) without its โน้ตแปะ (objects with `note: true`): notes are reminders on the screen and
 * never go into a file, a print PDF, a thumbnail, a video or a template (6ก, src/editor/drawing.ts). Returns the
 * JSON unchanged when it has none.
 */
export function withoutNotes(json: string): string {
  if (!json.includes('"note":true')) return json;
  const data = JSON.parse(json) as { objects?: unknown[]; extraPages?: Array<{ objects?: unknown[] }> };
  const keep = (list: unknown[] | undefined) => (list ?? []).filter(o => !(o && (o as { note?: unknown }).note === true));
  data.objects = keep(data.objects);
  if (Array.isArray(data.extraPages)) data.extraPages = data.extraPages.map(p => ({ ...p, objects: keep(p.objects) }));
  return JSON.stringify(data);
}

/** how many notes the design has (said in the export sheet: they are not in the file) */
export const noteCount = (json: string) => (json.match(/"note":true/g) ?? []).length;

/**
 * Drawing-tool data an app from before เครื่องมือ cannot keep: โน้ตแปะ (`note`), หัวลูกศร (`arrowStart`/`arrowEnd`)
 * and pen strokes (`pen`) — it saves objects without them (a note would become a printed text box, heads vanish).
 * This app writes `"draw":1` once it knows them (the JSON it opened had it, or the work has any of them; never turned
 * off by undo — BUG-48's lesson); the server refuses a save without it over a design that has them (D1 has_draw, 0014).
 */
export const hasDrawMarks = (json: string) => /"note":true|"arrowStart":true|"arrowEnd":true|"pen":"/.test(json);

/**
 * เอฟเฟกต์ข้อความ (src/editor/textFx.ts, `textFx` on a text): an app from before it saves the text without it — the
 * look Fabric already has stays (shadow, outline), but a พื้นหลัง box and the effect's settings are lost. Guarded the
 * same way as the drawing tools: `"textfx":1` written once the app knows them, D1 has_text_fx (0020).
 */
export const hasTextFx = (json: string) => /"textFx":\{/.test(json);

/**
 * ตาราง (src/table/tableModel.ts, `table` on a group): an app from before it keeps the group (it looks the same) but
 * saves it without its data — the table can no longer be edited. Guarded the same way: `"tables":1` written once the
 * app knows them, D1 has_tables (0027).
 */
export const hasTables = (json: string) => /"table":\{/.test(json);

/*
 * เลขรุ่นของงาน (G2, choice 28 ต.ค. 2569 14:20; D1 designs.fmt, 0028): one number instead of a flag per feature.
 * The app writes `"fmt": APP_FMT` (what it knows; never changed by undo — it is the app's, not the work's). A work
 * NEEDS the number of the newest thing in it (fmtNeeded); the server refuses a save from an app whose number is
 * lower than what the stored work needs. A new feature an older app would lose: raise APP_FMT and add it here.
 *   1  เอกสาร (0028): ช่องข้อมูล `field`, ข้อความสองภาษา `i18n`, คำแปลในช่องตาราง `tr`
 */
export const APP_FMT = 1;
export const fmtNeeded = (json: string): number => /"field":\{|"i18n":\{|"tr":\{"th"/.test(json) ? 1 : 0;
/** the number an app wrote in a work's JSON (0 = an app from before 0028 wrote it: no `fmt`) */
export function fmtOf(json: string): number {
  try {
    const f = (JSON.parse(json) as { fmt?: unknown }).fmt;
    return Number.isInteger(f) && (f as number) >= 0 ? f as number : 0;
  } catch { return 0; }
}

/**
 * BUG-69: while a text is being typed in, Fabric turns its handles off and locks it (selectable/hasControls false,
 * lockMovementX/Y true — its own `_savedProps` hold the real ones). A snapshot taken then (typing records one, the
 * autosave reads one) kept that lock: one undo, or a page saved mid-typing, gave back a text that could not be
 * chosen, moved or resized. So `read` runs with the real values, and the editing ones are put back right after.
 */
export const EDITING_LOCKS = ['selectable', 'hasControls', 'lockMovementX', 'lockMovementY'] as const;
type Editing = { isEditing?: boolean; _savedProps?: Record<string, unknown> } & Partial<Record<(typeof EDITING_LOCKS)[number], unknown>>;
export function asNotEditing<T>(objects: readonly object[], read: () => T): T {
  const now = (objects as Editing[]).filter(o => o.isEditing && o._savedProps)
    .map(o => ({ o, was: Object.fromEntries(EDITING_LOCKS.map(k => [k, o[k]])) }));
  for (const { o } of now) for (const k of EDITING_LOCKS) if (k in o._savedProps!) o[k] = o._savedProps![k];
  try { return read(); }
  finally { for (const { o, was } of now) Object.assign(o, was); }
}
